/**
 * Stellaris Gallery Swipe  v2.0.0
 * ================================
 * Touch / mouse / keyboard swipe for the product color gallery.
 * Full lightbox with vertical-swipe-to-close, pinch-to-zoom, momentum.
 */

(function () {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────────────
  const SWIPE_THRESHOLD    = 40;   // px — min horizontal drag to navigate
  const VELOCITY_MIN       = 0.25; // px/ms — min flick velocity
  const CLOSE_SWIPE_DOWN   = 90;   // px — vertical drag to dismiss lightbox
  const CLOSE_VELOCITY_Y   = 0.4;  // px/ms — fast-flick down also dismisses

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const qs    = (s, r = document) => r.querySelector(s);
  const qsa   = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  // ─── Per-gallery ────────────────────────────────────────────────────────────
  function initGallery(gallery) {
    if (gallery._swipeInit) return;
    gallery._swipeInit = true;

    const mainWrapper   = qs('[data-main-image-wrapper]', gallery);
    const mainContainer = qs('[data-main-image-container]', gallery);
    if (!mainWrapper) return;

    // ── Image list ───────────────────────────────────────────────────────────
    function getImages() {
      const group = qs('[data-thumbnails-group].is-active', gallery)
                 || gallery;
      return qsa('[data-thumb-btn]', group)
        .map(b => ({ src: b.dataset.imageSrc, srcset: b.dataset.imageSrcset || '', btn: b }));
    }

    function currentIndex() {
      const imgs = getImages();
      const i = imgs.findIndex(im => im.btn.classList.contains('is-active'));
      return i < 0 ? 0 : i;
    }

    function goToIndex(idx) {
      const imgs = getImages();
      if (!imgs.length) return;
      idx = clamp(idx, 0, imgs.length - 1);
      const { src, srcset, btn } = imgs[idx];

      // Cross-fade main image: instant-hide → swap src → fade-in
      const mainImg = qs('img[data-main-image]', gallery)
                   || qs('.product-color-gallery__image', mainWrapper);
      if (mainImg) {
        mainImg.style.transition = 'none';
        mainImg.style.opacity    = '0';
        mainImg.src    = src;
        mainImg.srcset = srcset;
        const showImg = () => {
          mainImg.style.transition = 'opacity 0.25s ease';
          mainImg.style.opacity    = '1';
        };
        mainImg.onload = showImg;
        if (mainImg.complete && mainImg.naturalWidth) showImg();
      }

      // Activate thumb
      const group = btn.closest('[data-thumbnails-group]');
      if (group) qsa('[data-thumb-btn]', group).forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

      updateDots(idx, imgs.length);
    }

    // ── Pagination dots ───────────────────────────────────────────────────────
    let dotsEl = qs('.gallery-swipe__dots', gallery);
    if (!dotsEl) {
      dotsEl = document.createElement('div');
      dotsEl.className = 'gallery-swipe__dots';
      mainContainer && mainContainer.appendChild(dotsEl);
    }

    function rebuildDots() {
      const imgs = getImages();
      dotsEl.innerHTML = '';
      imgs.forEach((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'gallery-swipe__dot';
        d.setAttribute('aria-label', `View image ${i + 1}`);
        d.addEventListener('click', () => goToIndex(i));
        dotsEl.appendChild(d);
      });
      updateDots(currentIndex(), imgs.length);
    }

    function updateDots(idx, total) {
      qsa('.gallery-swipe__dot', dotsEl).forEach((d, i) =>
        d.classList.toggle('is-active', i === idx));
      dotsEl.style.display = total > 1 ? 'flex' : 'none';
    }

    rebuildDots();

    // ── Main gallery swipe (touch + mouse) ───────────────────────────────────
    let gsStartX = 0, gsStartY = 0, gsLastX = 0, gsLastT = 0;
    let gsVelX = 0, gsDragging = false, gsAxisLocked = null;

    function gsDown(e) {
      if (e.button && e.button !== 0) return;
      const p = e.touches ? e.touches[0] : e;
      gsStartX = gsLastX = p.clientX;
      gsStartY = p.clientY;
      gsLastT = Date.now();
      gsVelX = 0; gsDragging = true; gsAxisLocked = null;
      mainWrapper.style.userSelect = 'none';
      mainWrapper.style.cursor = 'grabbing';
    }

    function gsMove(e) {
      if (!gsDragging) return;
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - gsStartX;
      const dy = p.clientY - gsStartY;
      if (gsAxisLocked === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6))
        gsAxisLocked = Math.abs(dy) > Math.abs(dx) ? 'v' : 'h';
      if (gsAxisLocked === 'v') return;
      if (e.cancelable) e.preventDefault();
      const now = Date.now();
      gsVelX = (p.clientX - gsLastX) / Math.max(1, now - gsLastT);
      gsLastX = p.clientX; gsLastT = now;
    }

    function gsUp(e) {
      if (!gsDragging) return;
      gsDragging = false;
      mainWrapper.style.userSelect = '';
      mainWrapper.style.cursor = 'grab';
      if (gsAxisLocked === 'v') return;
      const p = e.changedTouches ? e.changedTouches[0] : e;
      const dx = p.clientX - gsStartX;
      if (Math.abs(dx) > SWIPE_THRESHOLD || Math.abs(gsVelX) > VELOCITY_MIN) {
        const imgs = getImages();
        goToIndex(clamp(currentIndex() + (dx < 0 ? 1 : -1), 0, imgs.length - 1));
      }
    }

    mainWrapper.style.cursor = 'grab';
    mainWrapper.addEventListener('touchstart', gsDown, { passive: true });
    mainWrapper.addEventListener('touchmove',  gsMove, { passive: false });
    mainWrapper.addEventListener('touchend',   gsUp,   { passive: true });
    mainWrapper.addEventListener('mousedown',  gsDown);
    document.addEventListener('mousemove', e => { if (gsDragging) gsMove(e); });
    document.addEventListener('mouseup',   e => { if (gsDragging) gsUp(e); });

    // ── Keyboard ─────────────────────────────────────────────────────────────
    mainWrapper.setAttribute('tabindex', '0');
    mainWrapper.setAttribute('role', 'region');
    mainWrapper.setAttribute('aria-label', 'Product images – use arrow keys');
    mainWrapper.addEventListener('keydown', e => {
      const imgs = getImages(), idx = currentIndex();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown')  { e.preventDefault(); goToIndex(clamp(idx + 1, 0, imgs.length - 1)); }
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')    { e.preventDefault(); goToIndex(clamp(idx - 1, 0, imgs.length - 1)); }
      if (e.key === 'Enter' || e.key === ' ')                { e.preventDefault(); openLightbox(currentIndex()); }
    });

    // ── Nav arrows (show on hover) ────────────────────────────────────────────
    function ensureNavArrows() {
      if (qs('.gallery-swipe__prev', mainContainer)) return;
      ['prev', 'next'].forEach(dir => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `gallery-swipe__arrow gallery-swipe__${dir}`;
        btn.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
        btn.innerHTML = dir === 'prev'
          ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>'
          : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
        btn.addEventListener('click', () => {
          const imgs = getImages();
          goToIndex(clamp(currentIndex() + (dir === 'next' ? 1 : -1), 0, imgs.length - 1));
        });
        mainContainer && mainContainer.appendChild(btn);
      });
    }
    ensureNavArrows();

    // ── Re-init on color swatch change ────────────────────────────────────────
    gallery.addEventListener('click', e => {
      if (e.target.closest('[data-color-swatch]'))
        setTimeout(() => { rebuildDots(); ensureNavArrows(); }, 50);
    });

    // ────────────────────────────────────────────────────────────────────────
    // ── LIGHTBOX ────────────────────────────────────────────────────────────
    // ────────────────────────────────────────────────────────────────────────
    let lb        = null;   // the lightbox element
    let lbIdx     = 0;
    let lbScale   = 1;
    let lbPinch0  = null;
    let lbScrollY = 0;      // saved window scroll position

    // ── Build (first call only) ───────────────────────────────────────────────
    function buildLightbox() {
      if (lb) return;
      const existing = document.getElementById('gallery-lightbox');
      if (existing) { lb = existing; return; }

      lb = document.createElement('div');
      lb.id = 'gallery-lightbox';
      lb.className = 'gallery-lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Image viewer');

      // ── Flat structure: every control is a direct fixed child ──────────────
      lb.innerHTML = `
        <div class="glb-backdrop"></div>
        <div class="glb-stage">
          <img class="glb-img" src="" alt="" draggable="false">
        </div>
        <button type="button" class="glb-close" aria-label="Close image viewer">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          <span class="glb-close__label">Close</span>
        </button>
        <button type="button" class="glb-nav glb-prev" aria-label="Previous image">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button type="button" class="glb-nav glb-next" aria-label="Next image">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <div class="glb-footer">
          <span class="glb-counter"></span>
          <span class="glb-hint">Swipe down to close · Pinch to zoom</span>
        </div>
      `;

      document.body.appendChild(lb);

      // ── Event bindings ──────────────────────────────────────────────────────
      // Backdrop click → close
      qs('.glb-backdrop', lb).addEventListener('click', closeLightbox);

      // Close button — direct listener, highest z-index in flat structure
      qs('.glb-close', lb).addEventListener('click', closeLightbox);

      // Nav arrows
      qs('.glb-prev', lb).addEventListener('click', () => lbNavigate(-1));
      qs('.glb-next', lb).addEventListener('click', () => lbNavigate(1));

      // Keyboard
      document.addEventListener('keydown', e => {
        if (!lb || !lb.classList.contains('is-open')) return;
        if (e.key === 'Escape')     { e.preventDefault(); closeLightbox(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); lbNavigate(1);   }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); lbNavigate(-1);  }
      });

      // ── Touch / drag on the stage ─────────────────────────────────────────
      const stage = qs('.glb-stage', lb);
      let lbStartX = 0, lbStartY = 0;
      let lbLastX  = 0, lbLastY  = 0, lbLastT = 0;
      let lbVelX   = 0, lbVelY   = 0;
      let lbDown   = false, lbAxisLocked = null;
      let lbLastTap = 0;

      function pointerStart(cx, cy) {
        lbStartX = lbLastX = cx;
        lbStartY = lbLastY = cy;
        lbLastT  = Date.now();
        lbVelX = lbVelY = 0;
        lbDown = true; lbAxisLocked = null;
        stage.classList.add('dragging');
      }

      function pointerMove(cx, cy) {
        if (!lbDown) return;
        const now = Date.now();
        const dt  = Math.max(1, now - lbLastT);
        lbVelX = (cx - lbLastX) / dt;
        lbVelY = (cy - lbLastY) / dt;
        lbLastX = cx; lbLastY = cy; lbLastT = now;

        const dx = cx - lbStartX;
        const dy = cy - lbStartY;
        if (lbAxisLocked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8))
          lbAxisLocked = Math.abs(dy) > Math.abs(dx) ? 'v' : 'h';

        // Visual feedback for vertical swipe-to-close
        if (lbAxisLocked === 'v' && dy > 0 && lbScale === 1) {
          const img = qs('.glb-img', lb);
          if (img) {
            const progress = Math.min(dy / CLOSE_SWIPE_DOWN, 1);
            img.style.transition = 'none';
            img.style.transform  = `translateY(${dy * 0.6}px) scale(${1 - progress * 0.15})`;
            lb.style.opacity = String(1 - progress * 0.4);
          }
        }
      }

      function pointerEnd(cx, cy) {
        if (!lbDown) return;
        lbDown = false;
        stage.classList.remove('dragging');

        const dx = cx - lbStartX;
        const dy = cy - lbStartY;

        // Reset visual feedback
        const img = qs('.glb-img', lb);
        if (img && lbAxisLocked === 'v') {
          img.style.transition = 'transform 0.3s ease';
          img.style.transform  = '';
        }
        lb.style.opacity = '';

        if (lbScale > 1) return; // ignore swipe when zoomed

        if (lbAxisLocked === 'v') {
          // Vertical: swipe down to close
          if ((dy > CLOSE_SWIPE_DOWN || lbVelY > CLOSE_VELOCITY_Y) && dy > 0) {
            closeLightbox();
          }
        } else if (lbAxisLocked === 'h') {
          // Horizontal: navigate
          if (Math.abs(dx) > SWIPE_THRESHOLD || Math.abs(lbVelX) > VELOCITY_MIN) {
            lbNavigate(dx < 0 ? 1 : -1);
          }
        }
      }

      // Touch events
      stage.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
          lbPinch0 = Math.hypot(
            e.touches[1].clientX - e.touches[0].clientX,
            e.touches[1].clientY - e.touches[0].clientY);
          return;
        }
        pointerStart(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: true });

      stage.addEventListener('touchmove', e => {
        // Pinch zoom
        if (e.touches.length === 2 && lbPinch0 !== null) {
          const dist = Math.hypot(
            e.touches[1].clientX - e.touches[0].clientX,
            e.touches[1].clientY - e.touches[0].clientY);
          lbScale = clamp(dist / lbPinch0, 1, 4);
          const img = qs('.glb-img', lb);
          if (img) { img.style.transition = 'none'; img.style.transform = `scale(${lbScale})`; }
          return;
        }
        if (!lbDown) return;
        if (e.cancelable) e.preventDefault();
        pointerMove(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: false });

      stage.addEventListener('touchend', e => {
        lbPinch0 = null;
        if (!lbDown) return;
        pointerEnd(
          e.changedTouches[0].clientX,
          e.changedTouches[0].clientY);
        // Double-tap to reset zoom
        const now = Date.now();
        if (now - lbLastTap < 280) {
          lbScale = 1;
          const img = qs('.glb-img', lb);
          if (img) { img.style.transition = 'transform 0.3s ease'; img.style.transform = 'scale(1)'; }
        }
        lbLastTap = now;
      }, { passive: true });

      // Mouse drag
      stage.addEventListener('mousedown', e => {
        if (e.button !== 0) return;
        pointerStart(e.clientX, e.clientY);
      });
      document.addEventListener('mousemove', e => {
        if (lbDown && lb && lb.classList.contains('is-open'))
          pointerMove(e.clientX, e.clientY);
      });
      document.addEventListener('mouseup', e => {
        if (lbDown && lb && lb.classList.contains('is-open'))
          pointerEnd(e.clientX, e.clientY);
      });
    }

    // ── Navigate inside lightbox ──────────────────────────────────────────────
    function lbNavigate(dir) {
      const imgs = getImages();
      lbGo(clamp(lbIdx + dir, 0, imgs.length - 1));
    }

    // ── Go to index inside lightbox ───────────────────────────────────────────
    function lbGo(idx) {
      const imgs = getImages();
      if (!imgs.length) return;
      lbIdx  = clamp(idx, 0, imgs.length - 1);
      lbScale = 1;

      const img = qs('.glb-img', lb);
      if (img) {
        // Reset transform
        img.style.transition = 'none';
        img.style.transform  = 'scale(1) translateY(0)';

        // Instant-hide so old image never bleeds through
        img.style.opacity = '0';

        img.src    = imgs[lbIdx].src;
        img.srcset = imgs[lbIdx].srcset || '';

        const reveal = () => {
          img.style.transition = 'opacity 0.3s ease';
          img.style.opacity    = '1';
        };
        img.onload = reveal;
        if (img.complete && img.naturalWidth) reveal();
      }

      // Counter
      const counter = qs('.glb-counter', lb);
      if (counter) counter.textContent = `${lbIdx + 1} / ${imgs.length}`;

      // Disable nav arrows at boundaries
      const prev = qs('.glb-prev', lb);
      const next = qs('.glb-next', lb);
      if (prev) prev.disabled = lbIdx === 0;
      if (next) next.disabled = lbIdx === imgs.length - 1;
    }

    // ── Open ──────────────────────────────────────────────────────────────────
    function openLightbox(idx) {
      buildLightbox();

      // Lock body scroll (save current position to avoid layout shift)
      lbScrollY = window.scrollY;
      document.body.style.overflow   = 'hidden';
      document.body.style.position   = 'fixed';
      document.body.style.top        = `-${lbScrollY}px`;
      document.body.style.width      = '100%';
      document.documentElement.style.overflow = 'hidden';

      lb.classList.add('is-open');
      lbGo(idx);

      // Focus close button
      setTimeout(() => {
        const closeBtn = qs('.glb-close', lb);
        if (closeBtn) closeBtn.focus();
      }, 60);
    }

    // ── Close ─────────────────────────────────────────────────────────────────
    function closeLightbox() {
      if (!lb || !lb.classList.contains('is-open')) return;
      lb.classList.remove('is-open');
      lb.style.opacity = '';   // reset drag-dim state

      // Restore scroll
      document.body.style.overflow   = '';
      document.body.style.position   = '';
      document.body.style.top        = '';
      document.body.style.width      = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, lbScrollY);

      // Reset image state
      const img = qs('.glb-img', lb);
      if (img) { img.style.transform = ''; img.style.opacity = ''; }
    }

    // ── Tap on main image opens lightbox ──────────────────────────────────────
    mainWrapper.addEventListener('click', e => {
      if (Math.abs((e.clientX || 0) - gsStartX) > 8) return;
      openLightbox(currentIndex());
    });

    // Zoom button
    const zoomBtn = qs('[data-zoom-btn]', gallery);
    if (zoomBtn) zoomBtn.addEventListener('click', () => openLightbox(currentIndex()));
  }

  // ─── Styles ─────────────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('gallery-swipe-styles')) return;
    const s = document.createElement('style');
    s.id = 'gallery-swipe-styles';
    s.textContent = `
      /* ── Gallery dots ───────────────────────────────────────────────── */
      .gallery-swipe__dots {
        position: absolute; bottom: 10px; left: 50%;
        transform: translateX(-50%); z-index: 20;
        display: flex; gap: 6px; pointer-events: none;
      }
      .gallery-swipe__dot {
        width: 7px; height: 7px; border-radius: 50%; border: none;
        background: rgba(255,255,255,0.45); cursor: pointer; padding: 0;
        pointer-events: all; transition: background 0.2s, transform 0.2s;
      }
      .gallery-swipe__dot.is-active { background: #fff; transform: scale(1.4); }

      /* ── Gallery nav arrows ─────────────────────────────────────────── */
      .gallery-swipe__arrow {
        position: absolute; top: 50%; transform: translateY(-50%); z-index: 20;
        width: 40px; height: 40px; border-radius: 50%; border: none;
        background: rgba(255,255,255,0.85); backdrop-filter: blur(6px);
        cursor: pointer; display: flex; align-items: center; justify-content: center;
        opacity: 0; transition: opacity 0.2s, background 0.2s, transform 0.2s;
        box-shadow: 0 2px 12px rgba(0,0,0,0.15);
      }
      .gallery-swipe__prev { left: 12px; }
      .gallery-swipe__next { right: 12px; }
      [data-main-image-container]:hover .gallery-swipe__arrow,
      [data-main-image-container]:focus-within .gallery-swipe__arrow { opacity: 1; }
      .gallery-swipe__arrow:hover { background: #fff; transform: translateY(-50%) scale(1.08); }

      /* ── Lightbox shell ─────────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed; inset: 0;
        z-index: 99999;
        display: none;           /* JS adds .is-open */
        background: rgba(0, 0, 0, 0.94); /* owns the scrim — no child dependency */
        isolation: isolate;
        overflow: hidden;
      }
      .gallery-lightbox.is-open { display: block; }

      /* ── Backdrop (layer 1) — click to close only ────────────────── */
      .glb-backdrop {
        position: absolute; inset: 0; z-index: 1;
        background: transparent;
        cursor: pointer;
      }

      /* ── Image stage (layer 2) ───────────────────────────────────── */
      .glb-stage {
        position: fixed; inset: 0; z-index: 2;
        display: flex; align-items: center; justify-content: center;
        cursor: grab;
        pointer-events: none;   /* backdrop receives clicks not covered by img */
      }
      .glb-stage.dragging { cursor: grabbing; }
      .glb-img {
        pointer-events: all;
        display: block;
        max-width: min(90vw, 1100px);
        max-height: 82vh;
        width: auto; height: auto;
        object-fit: contain;
        user-select: none;
        -webkit-user-drag: none;
        will-change: transform, opacity;
        transform-origin: center center;
      }

      /* ── Close button (layer 4 — highest) ───────────────────────── */
      /* Positioned top-right corner of the viewport, always visible */
      .glb-close {
        position: fixed;
        top: 16px; right: 16px;
        z-index: 4;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
        width: 56px; height: 56px;
        border-radius: 12px;
        border: 1px solid rgba(255,255,255,0.22);
        background: rgba(20, 20, 20, 0.75);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        color: #fff;
        font-size: 10px;
        font-family: sans-serif;
        font-weight: 600;
        letter-spacing: 0.5px;
        cursor: pointer;
        transition: background 0.2s, transform 0.15s;
        pointer-events: all;
      }
      .glb-close:hover {
        background: rgba(60, 60, 60, 0.9);
        transform: scale(1.08);
      }
      .glb-close:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
      }
      .glb-close svg { flex-shrink: 0; }
      .glb-close__label { text-transform: uppercase; line-height: 1; }

      /* ── Nav arrows (layer 3) ────────────────────────────────────── */
      .glb-nav {
        position: fixed; top: 50%; transform: translateY(-50%); z-index: 3;
        width: 52px; height: 52px; border-radius: 50%;
        border: 1px solid rgba(255,255,255,0.15);
        background: rgba(20,20,20,0.6);
        backdrop-filter: blur(8px);
        color: #fff; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        transition: background 0.2s, transform 0.15s;
        pointer-events: all;
      }
      .glb-nav:hover { background: rgba(60,60,60,0.85); transform: translateY(-50%) scale(1.08); }
      .glb-nav:disabled { opacity: 0.25; pointer-events: none; }
      .glb-prev { left: 12px; }
      .glb-next { right: 12px; }
      @media (max-width: 749px) {
        .glb-nav { width: 44px; height: 44px; }
        .glb-prev { left: 6px; }
        .glb-next { right: 6px; }
      }

      /* ── Footer: counter + hint (layer 3) ───────────────────────── */
      .glb-footer {
        position: fixed; bottom: 20px; left: 0; right: 0; z-index: 3;
        display: flex; flex-direction: column; align-items: center; gap: 4px;
        pointer-events: none;
      }
      .glb-counter {
        color: rgba(255,255,255,0.75);
        font-size: 14px; font-weight: 500; letter-spacing: 0.5px;
      }
      .glb-hint {
        color: rgba(255,255,255,0.3);
        font-size: 11px;
      }
      @media (min-width: 750px) { .glb-hint { display: none; } }
    `;
    document.head.appendChild(s);
  }

  // ─── Bootstrap ──────────────────────────────────────────────────────────────
  function scanAndInit() {
    qsa('.product-color-gallery').forEach(initGallery);
  }

  function init() {
    injectStyles();
    scanAndInit();
    document.addEventListener('shopify:section:load', scanAndInit);
    new MutationObserver(scanAndInit).observe(document.body, { childList: true, subtree: true });
  }

  // Clear any scroll locks left behind by bfcache restore or unexpected reload
  window.addEventListener('pageshow', function() {
    document.body.style.overflow   = '';
    document.body.style.position   = '';
    document.body.style.top        = '';
    document.body.style.width      = '';
    document.documentElement.style.overflow = '';
    var openLb = document.getElementById('gallery-lightbox');
    if (openLb) openLb.classList.remove('is-open');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
